import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MentorAuthContext = createContext();

const ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_MENTOR: 'SET_MENTOR',
};

const initialState = {
  mentor: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

function mentorAuthReducer(state, action) {
  switch (action.type) {
    case ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        mentor: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        mentor: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      };
    case ACTIONS.SET_MENTOR:
      return {
        ...state,
        mentor: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
      };
    default:
      return state;
  }
}

export const MentorAuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(mentorAuthReducer, initialState);

  // Verificar token al cargar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('mentor_token');
      if (!token) {
        dispatch({ type: ACTIONS.LOGOUT });
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/mentor/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        dispatch({ type: ACTIONS.SET_MENTOR, payload: response.data });
      } catch (error) {
        console.error('Error verificando autenticación:', error);
        localStorage.removeItem('mentor_token');
        dispatch({ type: ACTIONS.LOGOUT });
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    dispatch({ type: ACTIONS.LOGIN_START });
    try {
      const response = await axios.post(`${BACKEND_URL}/api/mentor/login`, {
        email,
        password,
      });

      const { access_token } = response.data;
      localStorage.setItem('mentor_token', access_token);

      // Obtener perfil del mentor
      const mentorResponse = await axios.get(`${BACKEND_URL}/api/mentor/me`, {
        headers: { Authorization: `Bearer ${access_token}` },
      });

      dispatch({ type: ACTIONS.LOGIN_SUCCESS, payload: mentorResponse.data });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al iniciar sesión';
      dispatch({ type: ACTIONS.LOGIN_FAILURE, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('mentor_token');
    dispatch({ type: ACTIONS.LOGOUT });
  };

  const getToken = () => {
    return localStorage.getItem('mentor_token');
  };

  const updateMentorProfile = (updatedData) => {
    dispatch({ type: ACTIONS.SET_MENTOR, payload: { ...state.mentor, ...updatedData } });
  };

  const value = {
    mentor: state.mentor,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    login,
    logout,
    getToken,
    updateMentorProfile,
  };

  return <MentorAuthContext.Provider value={value}>{children}</MentorAuthContext.Provider>;
};

export const useMentorAuth = () => {
  const context = useContext(MentorAuthContext);
  if (!context) {
    throw new Error('useMentorAuth must be used within MentorAuthProvider');
  }
  return context;
};
